import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { megaMenuCategories, navLinks } from '../../data/megaMenuData';

/**
 * Mobile hamburger menu with 3-level accordion:
 * categories → subcategories → products.
 */
const MobileMenu = ({ onClose }) => {
  const location = useLocation();
  const [productsOpen, setProductsOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState({});
  const [subcategoryOpen, setSubcategoryOpen] = useState({});

  const toggleCategory = (idx) =>
    setCategoryOpen((prev) => ({ ...prev, [idx]: !prev[idx] }));
  const toggleSubcategory = (key) =>
    setSubcategoryOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="md:hidden bg-white border-t border-cyan-500/10 shadow-lg max-h-[calc(100vh-5rem)] overflow-y-auto">
      <div className="px-4 py-4 space-y-1">
        {navLinks.map((link) =>
          link.hasDropdown ? (
            <MobileProductsAccordion
              key={link.path}
              link={link}
              location={location}
              productsOpen={productsOpen}
              setProductsOpen={setProductsOpen}
              categoryOpen={categoryOpen}
              subcategoryOpen={subcategoryOpen}
              toggleCategory={toggleCategory}
              toggleSubcategory={toggleSubcategory}
              onClose={onClose}
            />
          ) : (
            <Link
              key={link.path}
              to={link.path}
              onClick={onClose}
              className={`block px-4 py-3 min-h-[48px] flex items-center rounded-xl text-base font-medium transition-all ${
                location.pathname === link.path
                  ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 text-cyan-600 border border-cyan-500/20'
                  : 'text-gray-700 active:bg-cyan-50'
              }`}
              data-testid={`mobile-nav-${link.name.toLowerCase()}`}
            >
              {link.name}
            </Link>
          )
        )}
        <Link
          to="/contact"
          onClick={onClose}
          className="block px-4 py-4 min-h-[52px] mt-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold text-center shadow-lg shadow-amber-500/20"
          data-testid="mobile-get-quote"
        >
          Get Quote
        </Link>
      </div>
    </div>
  );
};

const MobileProductsAccordion = ({
  link,
  location,
  productsOpen,
  setProductsOpen,
  categoryOpen,
  subcategoryOpen,
  toggleCategory,
  toggleSubcategory,
  onClose,
}) => (
  <div className="border-b border-gray-100 last:border-b-0">
    <div className="flex items-stretch">
      <Link
        to={link.path}
        onClick={onClose}
        className={`flex-1 px-4 py-3 min-h-[48px] flex items-center rounded-xl text-base font-medium transition-all ${
          location.pathname === link.path ? 'text-cyan-600' : 'text-gray-700 active:bg-cyan-50'
        }`}
        data-testid="mobile-nav-products"
      >
        {link.name}
      </Link>
      <button
        onClick={() => setProductsOpen(!productsOpen)}
        aria-label="Toggle products menu"
        aria-expanded={productsOpen}
        className="px-4 min-h-[48px] flex items-center text-gray-600 active:bg-cyan-50 rounded-xl"
        data-testid="mobile-products-accordion-toggle"
      >
        <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${productsOpen ? 'rotate-180' : ''}`} />
      </button>
    </div>

    {productsOpen && (
      <div className="pb-2 pl-2 space-y-1">
        {megaMenuCategories.map((category, idx) => (
          <MobileCategoryBlock
            key={category.name}
            idx={idx}
            category={category}
            isOpen={!!categoryOpen[idx]}
            toggle={() => toggleCategory(idx)}
            subcategoryOpen={subcategoryOpen}
            toggleSubcategory={toggleSubcategory}
            onClose={onClose}
          />
        ))}
      </div>
    )}
  </div>
);

const MobileCategoryBlock = ({ idx, category, isOpen, toggle, subcategoryOpen, toggleSubcategory, onClose }) => {
  const hasPath = category.path && category.path !== '#';
  return (
    <div className="border-l-2 border-cyan-500/20 ml-2">
      <div className="flex items-stretch">
        {hasPath ? (
          <Link
            to={category.path}
            onClick={onClose}
            className="flex-1 px-4 py-3 min-h-[44px] flex items-center text-sm font-semibold text-gray-800 active:bg-cyan-50 rounded-lg"
            data-testid={`mobile-category-${idx}`}
          >
            {category.name}
          </Link>
        ) : (
          <button
            type="button"
            onClick={toggle}
            className="flex-1 px-4 py-3 min-h-[44px] flex items-center text-sm font-semibold text-gray-800 active:bg-cyan-50 rounded-lg text-left"
            data-testid={`mobile-category-${idx}`}
          >
            {category.name}
          </button>
        )}
        <button
          onClick={toggle}
          aria-label={`Toggle ${category.name}`}
          aria-expanded={isOpen}
          className="px-3 min-h-[44px] flex items-center text-gray-500 active:bg-cyan-50 rounded-lg"
        >
          <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {isOpen && category.subcategories && (
        <div className="pl-3 pb-2 space-y-1">
          {category.subcategories.map((subcat, subIdx) => {
            const subKey = `${idx}-${subIdx}`;
            return (
              <MobileSubcategoryBlock
                key={subcat.name}
                subcat={subcat}
                isOpen={!!subcategoryOpen[subKey]}
                toggle={() => toggleSubcategory(subKey)}
                onClose={onClose}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const MobileSubcategoryBlock = ({ subcat, isOpen, toggle, onClose }) => {
  const hasProducts = subcat.products && subcat.products.length > 0;
  return (
    <div className="border-l border-cyan-500/15 ml-2">
      <div className="flex items-stretch">
        <Link
          to={subcat.path}
          onClick={onClose}
          className="flex-1 px-3 py-2.5 min-h-[44px] flex items-center text-sm text-cyan-600 font-medium active:bg-cyan-50 rounded-lg"
        >
          {subcat.name}
        </Link>
        {hasProducts && (
          <button
            onClick={toggle}
            aria-label={`Toggle ${subcat.name}`}
            aria-expanded={isOpen}
            className="px-3 min-h-[44px] flex items-center text-gray-500 active:bg-cyan-50 rounded-lg"
          >
            <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {hasProducts && isOpen && (
        <div className="pl-4 pb-1 space-y-0.5">
          {subcat.products.map((prod) => (
            <Link
              key={prod.path}
              to={prod.path}
              onClick={onClose}
              className="block px-3 py-2.5 min-h-[44px] flex items-center text-xs text-gray-600 active:bg-cyan-50 active:text-cyan-600 rounded-lg"
            >
              • {prod.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileMenu;
